/* rs-infinite-scroll.js - continuous scroll for the collection product grid (loaded by snippets/section.main-collection.liquid;
   see the comment above its loader). launch readiness pass 2, 2026-09-24: moved out of the inline <script>; loaded with `defer`. The only
   observable difference: the numbered pagination is hidden at end-of-parse instead of at grid parse - it sits below the
   last row of cards, off-screen at both 390 and 1440 on load.

   2026-10-16 (John: "nav is in middle of page on desktop peppers category"): the pager is now hidden by a CLASS ON THE GRID
   (`rs-infinite-on`, styled by the <style> this file injects), not by an inline style on the pager node, and the pager is
   moved to the END of the grid. Why: the pager is a <layout-stack> custom element inside an <is-land on:visible>. Before it
   hydrates, is-land swaps every not-yet-defined custom element for a placeholder (`<is-land--layout-stack>`, same
   attributes) and, when its module lands, swaps the ORIGINAL node back in place. Whether this script ran before or after
   that swap was a race: lose it and `pag.style.display = 'none'` was written to the placeholder, which is-land then threw
   away - the original came back with no inline style, display:flex, at its parse position, and page 2+ was appended
   BELOW it, so "1 2 3 >" sat in the middle of the page (measured on live: pepper and squash showed it, tomato did not, and
   a later run showed the opposite - a race, not a page difference). A class on the grid survives the swap because the grid
   is a plain <div>; the pager being last means that if the fetch ever fails and it is restored, it is below every loaded
   card. Also re-runs after a filter/sort re-render (`collection:reloaded`, the Section Rendering API replaces the grid).

   2026-10-16, launch audit CWV-DESKTOP-INP-1 + A11Y-MANUAL-6:
   - Page 2+ is fetched through the Section Rendering API (`?section_id=<template-scoped id>&page=N`, the same endpoint
     assets/list.filter-grid.js uses for filters) instead of the whole document: on pepper p2 that is ~450 KB of grid instead
     of ~790 KB of page, and header/footer/app-embed JSON never goes through DOMParser. The id comes from the grid's
     <item-grid data-section-id> ancestor (e.g. `template--...__main-collection`); the bare key `main-collection` renders the
     section WITHOUT the template's blocks - 49 KB and an empty grid - so it is never guessed. No id, or a section response
     with no cards, falls back to the full-page fetch for that same page; a failed fetch still restores the numbered pager.
   - The pager is now a light-DOM <nav class="element-pagination__nav" aria-label="Pagination"> (snippets/element.pagination.liquid)
     around the <layout-stack>. The nav, not the inner stack, is what is moved to the end and hidden - hiding only the stack
     would leave an empty "Pagination" landmark announced on every collection page. Page counting still reads the inner
     `.element-pagination-page` text. Works with either version of the snippet (`closest()` falls back to the stack).

   rareseeds 2026-10-21 (PLA-003, post-launch audit: CLS up to 0.95 on multi-page CLPs; stall after a jump past the sentinel):
   - RESERVE BEFORE FETCH. load() inserts an aria-hidden placeholder (full flex row, height = rows x the measured row pitch
     of the cards already on the page; the last page's row count comes from data-rs-items / data-rs-page-size on the grid,
     written by section.main-collection.liquid from paginate.items / paginate.page_size) before the sentinel, then fetches.
     The fetched cards replace the placeholder in the same task, so the description / swipe strip / footer below the grid
     are pushed down while they are still off-screen, not under the shopper when a ~450 KB response lands on cellular.
   - PREFETCH. Page N+1 is requested as soon as page N is inserted (parsed cards cached per page), so when the sentinel
     comes round the insert is instant; the lookahead is two viewports (min 1,600 px) instead of 800 px.
   - NO DEAD ZONE. The observer's rootMargin is 100000px on top, so a sentinel that is ABOVE the viewport (End key, a fast
     fling into the 2,600 px of newsletter + footer, an in-page anchor, back-navigation restoring scrollY on a 40-card
     page) counts as intersecting and still loads; a passive rAF-throttled scroll/resize check backs it up. Before, the
     sentinel sat at -1,962 px, the pager was hidden by .rs-infinite-on, and 84 of 124 tomatoes were unreachable.
     When that insert lands above the viewport, browsers without scroll anchoring (Safari) get a scrollBy of the reserved
     height in the same task, so the footer the shopper reached does not drop by a page (review R1).
   - A prefetch that fails is evicted from the per-page cache, so load() re-requests it instead of inheriting the
     rejection and downgrading the session to full-document fetches.
   - The template-scoped section id is re-read at every load() (resolveSectionId ran only when useSection was already
     true, so a grid whose <item-grid> is-land had not upgraded at setup fetched full documents for the whole session). */
(function () {
  var STYLE_ID = 'rs-infinite-style';
  function ensureStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var st = document.createElement('style');
    st.id = STYLE_ID;
    /* two classes beat .element-pagination { display: flex } in element.pagination.liquid without !important;
       the is-land placeholder carries the same class attribute, so it is matched whichever node is in the DOM.
       The nav wrapper is hidden too, so the landmark leaves the accessibility tree while JS drives paging. */
    st.textContent = '.collection-grid.rs-infinite-on .element-pagination__nav,.collection-grid.rs-infinite-on .element-pagination{display:none}';
    document.head.appendChild(st);
  }

  var teardown = null; // rareseeds 2026-10-21 (PLA-003): the previous grid's listeners, dropped when a filter swaps the grid

  function setup() {
    var grid = document.querySelector('.collection-grid');
    if (!grid || grid.getAttribute('data-rs-infinite') === '1') return;
    if (teardown) teardown();
    var pag = grid.querySelector('.element-pagination');
    if (!pag) return; // single page - nothing to do
    var pagWrap = pag.closest('.element-pagination__nav') || pag; // the <nav> landmark when the snippet renders one

    var nums = [];
    pag.querySelectorAll('.element-pagination-page').forEach(function (a) {
      var n = parseInt((a.textContent || '').trim(), 10);
      if (!isNaN(n)) nums.push(n);
    });
    var totalPages = nums.length ? Math.max.apply(null, nums) : Infinity;

    function currentPage() {
      var p = new URLSearchParams(location.search).get('page');
      return p ? parseInt(p, 10) : 1;
    }
    var nextPage = currentPage() + 1;
    if (nextPage > totalPages) return;

    /* Section Rendering API id: template-scoped, read from the DOM (never the bare section key - see header). */
    var itemGrid = grid.closest('item-grid') || document.querySelector('item-grid[data-section-id]');
    var sectionId = itemGrid ? (itemGrid.getAttribute('data-section-id') || '') : '';
    /* Review 2026-10-16: in 2 of 9 desktop runs the id read empty at setup() (is-land had not yet
       upgraded <item-grid>), so the whole session fell back to full-page fetches. Re-read it lazily
       the first time a page is actually requested. */
    function resolveSectionId() {
      if (sectionId) return sectionId;
      var ig = grid.closest('item-grid') || document.querySelector('item-grid[data-section-id]');
      sectionId = ig ? (ig.getAttribute('data-section-id') || '') : '';
      if (sectionId) useSection = true;
      return sectionId;
    }

    function urlForPage(n, sectionOnly) {
      var u = new URL(location.href);
      u.hash = '';
      Array.from(u.searchParams.keys()).forEach(function (k) {
        if (/^(utm_|_su_rec|fbclid|gclid|msclkid)/.test(k)) u.searchParams.delete(k); // tracking only; filters/sort stay
      });
      u.searchParams.set('page', n);
      u.searchParams.delete('view');
      if (sectionOnly && resolveSectionId()) u.searchParams.set('section_id', sectionId);
      return u.toString();
    }

    var loading = false, done = false, io = null;
    var pages = {}; // rareseeds 2026-10-21 (PLA-003): page+mode -> promise of parsed cards; a page is fetched and appended once
    var useSection = !!sectionId; // flips off for the rest of this grid if a section response ever comes back without cards
    grid.setAttribute('data-rs-infinite', '1');
    ensureStyle();
    grid.classList.add('rs-infinite-on'); // JS takes over; the class comes off again only in fallback()

    var sentinel = document.createElement('div');
    sentinel.className = 'rs-infinite-sentinel';
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'flex:0 0 100%;width:100%;height:1px;';
    grid.appendChild(sentinel);
    grid.appendChild(pagWrap); // pager last: new cards go in before the sentinel, so a restored pager is always at the very bottom

    /* rareseeds 2026-10-21 (PLA-003): height reservation. The grid is a flex-wrap row of .grid-item cards, so a
       full-width flex child holds exactly the rows the next page will fill. Column count and row pitch are measured
       from the cards already laid out (offsetTop groups), so density toggles and viewport width are always honoured. */
    var AHEAD = Math.max(1600, window.innerHeight * 2); // lookahead below the viewport, px
    var perPage = parseInt(grid.getAttribute('data-rs-page-size'), 10) || grid.querySelectorAll('.grid-item').length || 40;
    var totalItems = parseInt(grid.getAttribute('data-rs-items'), 10) || 0;
    var placeholder = null;

    function cardsOnPage(n) {
      if (!totalItems) return perPage;
      return Math.max(1, Math.min(perPage, totalItems - (n - 1) * perPage));
    }

    function rowMetrics() {
      var cards = grid.querySelectorAll('.grid-item');
      var n = cards.length;
      if (!n) return null;
      var firstTop = cards[0].offsetTop, prevTop = firstTop, cols = 0, rows = 1, i, t;
      for (i = 0; i < n; i++) {
        t = cards[i].offsetTop;
        if (Math.abs(t - firstTop) < 2) cols++;
        if (t - prevTop > 2) { rows++; prevTop = t; }
      }
      var pitch;
      if (rows > 1) pitch = (prevTop - firstTop) / (rows - 1);
      else {
        var cs = getComputedStyle(cards[0]);
        pitch = cards[0].getBoundingClientRect().height + (parseFloat(cs.marginTop) || 0) + (parseFloat(cs.marginBottom) || 0);
      }
      return { cols: Math.max(1, cols), pitch: Math.max(1, pitch) };
    }

    /* rareseeds 2026-10-21 (PLA-003, review R1): when the placeholder goes in ABOVE the viewport (the shopper jumped past
       the sentinel into the footer), Chrome and Firefox keep the content under the shopper still through scroll anchoring;
       Safari has no overflow-anchor, so without help the footer would drop by a full page height (~5,900 px at 390) and
       leave the shopper on the empty placeholder. Detected per call (cheap; a stylesheet could switch anchoring off). */
    function browserAnchors() {
      try {
        if (!(window.CSS && CSS.supports && CSS.supports('overflow-anchor', 'auto'))) return false;
        return getComputedStyle(document.documentElement).overflowAnchor !== 'none' && getComputedStyle(document.body).overflowAnchor !== 'none';
      } catch (e) { return false; }
    }

    function reserve(page) {
      if (placeholder) return;
      var m = rowMetrics();
      if (!m) return;
      var above = sentinel.getBoundingClientRect().top < 0; // the whole insert lands above the viewport
      placeholder = document.createElement('div');
      placeholder.className = 'rs-infinite-placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      placeholder.style.cssText = 'flex:0 0 100%;width:100%;box-sizing:border-box;height:' + Math.round(Math.ceil(cardsOnPage(page) / m.cols) * m.pitch) + 'px;';
      grid.insertBefore(placeholder, sentinel);
      if (above && !browserAnchors()) {
        /* same task as the insert, so the frame paints with the shopper's content where it was; the cards later replace
           the placeholder at the same height, so no second correction is needed. scroll-behavior pinned to auto for
           the one call in case a stylesheet ever asks for smooth scrolling on the document. */
        var root = document.documentElement, prev = root.style.scrollBehavior;
        root.style.scrollBehavior = 'auto';
        window.scrollBy(0, placeholder.offsetHeight);
        root.style.scrollBehavior = prev;
      }
    }

    function release() {
      if (placeholder && placeholder.parentNode) placeholder.parentNode.removeChild(placeholder);
      placeholder = null;
    }

    function finish() {
      done = true;
      if (io) io.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (teardown === finish) teardown = null;
    }
    teardown = finish;

    function fallback() {
      finish();
      release();
      grid.classList.remove('rs-infinite-on');
    }

    /* A section fetch that fails or comes back without a grid (unknown id, odd template, endpoint hiccup) is retried
       once as the full document - the pre-2026-10-16 path - for the same page, and section fetches are off from then on. */
    function retryAsFullPage() {
      useSection = false;
      loading = false;
      load();
    }

    /* One request per page and mode, cached so a prefetched page is appended from memory when the sentinel comes round.
       Resolves to the page's cards (an empty array when the response has no grid); rejects on a network/HTTP failure. */
    function fetchPage(page, viaSection) {
      var key = page + (viaSection ? 's' : 'f');
      if (!pages[key]) {
        var p = fetch(urlForPage(page, viaSection), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var newGrid = doc.querySelector('.collection-grid');
            return newGrid ? Array.prototype.slice.call(newGrid.querySelectorAll('.grid-item')) : [];
          });
        pages[key] = p;
        /* rareseeds 2026-10-21 (PLA-003, review): a rejected promise is EVICTED, never cached. Otherwise one blip during
           a speculative prefetch would hand load() the same rejection when the sentinel came round, and that would
           downgrade the whole session to full-document fetches (retryAsFullPage) or restore the pager (fallback). */
        p.catch(function () { if (pages[key] === p) delete pages[key]; });
      }
      return pages[key];
    }

    function prefetch() {
      if (done || nextPage > totalPages) return;
      /* a failure here is dropped (the entry is evicted by fetchPage), so load() issues a fresh request for the page */
      fetchPage(nextPage, useSection).catch(function () {});
    }

    /* The sentinel is "due" anywhere above the viewport bottom + lookahead - negative tops included, so a jump past it counts. */
    function check() {
      if (done) return;
      if (!grid.isConnected) { finish(); return; }
      if (sentinel.getBoundingClientRect().top < window.innerHeight + AHEAD) load();
    }

    function load() {
      if (loading || done || nextPage > totalPages) return;
      loading = true;
      var page = nextPage;
      resolveSectionId(); // late is-land upgrade: decide section vs full page on what the DOM says NOW
      var viaSection = useSection;
      reserve(page);
      fetchPage(page, viaSection)
        .then(function (cards) {
          if (!cards.length) {
            if (viaSection) return retryAsFullPage();
            release();
            loading = false;
            finish();
            return;
          }
          var frag = document.createDocumentFragment();
          cards.forEach(function (c) { frag.appendChild(c); });
          grid.insertBefore(frag, placeholder || sentinel);
          release(); // same task as the insert: one layout, the reserved rows become the real ones
          nextPage++;
          loading = false;
          if (nextPage > totalPages) finish();
          else { prefetch(); check(); }
        })
        .catch(function () {
          if (viaSection) return retryAsFullPage();
          loading = false;
          fallback(); // the full-page fetch failed too: numbered pagination comes back, at the bottom of the grid
        });
    }

    var ticking = false;
    function onScroll() {
      if (ticking || done) return;
      ticking = true;
      window.requestAnimationFrame(function () { ticking = false; check(); });
    }

    io = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) load();
    }, { rootMargin: '100000px 0px ' + AHEAD + 'px 0px' });
    io.observe(sentinel);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
  }

  setup();
  /* item-grid (assets/list.filter-grid.js) replaces #AjaxContent's HTML on every filter/sort change and then fires this;
     the new grid has no sentinel and a visible pager until setup() runs again. */
  document.addEventListener('collection:reloaded', setup);
})();
